import React, { useEffect, useState } from 'react'
import { Avatar, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert'
import WhatshotIcon from '@mui/icons-material/Whatshot'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import getRows from './getRows'
import AddNewRisk from './AddNewRisk'
import { getRAGband } from './getRAGcolor'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import StatTile from '../../userPages/dashboard/managerOverview/StatTile'
import { CQC_ACTIVITY_SHORT_LABEL, keyQuestionForStatement, useCqcStatements } from './cqcLinkage'

// Same staggered entrance as the manager overview / employee dashboard
const sectionMotion = (index) => ({
  component: motion.div,
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay: 0.08 * index, ease: 'easeOut' },
})

const RagPill = ({ rating, soft }) => {
  const band = getRAGband(rating)
  if (!rating) {
    return (
      <Typography variant="smallGrey" sx={{ fontStyle: 'italic' }}>
        Not scored
      </Typography>
    )
  }
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 5,
        bgcolor: soft ? `${band.color}14` : `${band.color}1F`,
        color: band.color,
        fontWeight: 700,
        fontSize: '0.8rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: band.color }}
      />
      {rating} &middot; {band.label}
    </Box>
  )
}

const Register = () => {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 })
  const [addNewRisk, setAddNewRisk] = useState(false)
  const navigate = useNavigate()
  const { statements } = useCqcStatements()

  const loadRows = (model = paginationModel) =>
    getRows({
      page: model.page,
      limit: model.pageSize,
      setRows,
      setTotal,
      setStats,
      setLoading,
    })

  useEffect(() => {
    loadRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel])

  const columns = [
    {
      field: 'riskID',
      headerName: 'ID',
      width: 70,
      renderCell: (params) => (
        <Typography variant="smallGrey" sx={{ fontWeight: 600 }}>
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'title',
      headerName: 'Risk',
      flex: 1.4,
      minWidth: 220,
      valueGetter: (value, row) => value || row.description || '',
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.row.title || 'Untitled Risk'}
          </Typography>
          {params.row.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip size="small" variant="outlined" label={params.value} sx={{ borderRadius: 1 }} />
        ) : null,
    },
    {
      field: 'riskOwner',
      headerName: 'Owner',
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center">
          <Avatar src={params.row.riskOwner?.photograph} sx={{ width: 24, height: 24 }} />
          <Typography variant="body2">
            {params.row.riskOwner?.firstName} {params.row.riskOwner?.lastName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'cqcQualityStatementKeys',
      headerName: 'CQC',
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const keyQuestions = [
          ...new Set(
            (params.value ?? [])
              .map((key) => keyQuestionForStatement(key, statements))
              .filter(Boolean),
          ),
        ]
        const activity = CQC_ACTIVITY_SHORT_LABEL[params.row.cqcActivityType]
        if (keyQuestions.length === 0 && !activity) {
          return <Typography variant="smallGrey">Default mapping</Typography>
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {activity && (
              <Chip
                size="small"
                label={activity}
                sx={{
                  borderRadius: 1,
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: '#1E7F88',
                  borderColor: '#1E7F88',
                }}
                variant="outlined"
              />
            )}
            {keyQuestions.map((kq) => (
              <Chip
                key={kq}
                size="small"
                label={kq}
                sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }}
              />
            ))}
          </Box>
        )
      },
    },
    {
      field: 'currentRating',
      headerName: 'Current',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <RagPill rating={params.value} />,
    },
    {
      field: 'targetRating',
      headerName: 'Target',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <RagPill rating={params.value} soft />,
    },
    {
      field: 'lastReview',
      headerName: 'Last Review',
      width: 130,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.value ? moment(params.value).format('Do MMM YYYY') : 'Never'}
          </Typography>
          {params.row.reviewOverdue && (
            <Typography
              variant="caption"
              sx={{ color: '#E32525', fontWeight: 700, display: 'block', lineHeight: 1 }}
            >
              Review overdue
            </Typography>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Stack gap={2}>
      <Box
        {...sectionMotion(0)}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
      >
        <Box>
          <Typography variant="title2">Risk Register</Typography>
          <Typography variant="smallGrey">
            Identify, score and review organisational risks &mdash; linked risks feed the CQC
            Centre as live evidence.
          </Typography>
        </Box>
        <Button
          onClick={() => setAddNewRisk(true)}
          startIcon={<AddIcon />}
          color="primary"
          variant="contained"
        >
          New Risk
        </Button>
      </Box>

      <Grid container spacing={2} {...sectionMotion(1)}>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={CrisisAlertIcon}
            label="Risks on Register"
            value={stats?.total ?? 0}
            accent="#1E7F88"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={WhatshotIcon}
            label="High / Extreme"
            value={stats?.highRisks ?? 0}
            accent="#E32525"
            hint="Current score of 8 or more"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={EventRepeatIcon}
            label="Reviews Overdue"
            value={stats?.overdueReview ?? 0}
            accent="#F28B1F"
            hint="High risks 6-monthly, others yearly"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            icon={FactCheckIcon}
            label="Linked to CQC"
            value={stats?.cqcLinked ?? 0}
            accent="#1F3D7C"
            hint="Risks with a chosen activity or statement"
          />
        </Grid>
      </Grid>

      <Box {...sectionMotion(2)} sx={{ height: 620, width: '100%' }}>
        <CustomDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={58}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(params.id)}
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
          sx={{
            '.MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
        />
      </Box>

      <AddNewRisk
        open={addNewRisk}
        onClose={() => {
          setAddNewRisk(false)
          loadRows()
        }}
      />
    </Stack>
  )
}

export default Register
